import { FeedbackData } from '../types/feedback';

interface StorageStructure {
  [conversationId: string]: {
    [messageId: string]: FeedbackData;
  };
}

/**
 * SessionStorage manager for feedback persistence
 * Follows the existing pattern used in the application for conversation data
 */
export class FeedbackStorageManager {
  private static readonly STORAGE_KEY = 'conversation_feedback';

  /**
   * Save feedback data for a specific message in a conversation
   */
  static saveFeedback(conversationId: string, messageId: string, feedback: FeedbackData): void {
    try {
      const existing = this.getAllStoredData();
      
      if (!existing[conversationId]) {
        existing[conversationId] = {};
      }
      
      existing[conversationId][messageId] = {
        ...feedback,
        timestamp: new Date().toISOString()
      };
      
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(existing));
    } catch (error) {
      console.error('Failed to save feedback to sessionStorage:', error);
    }
  }

  /**
   * Get feedback data for a specific message
   */
  static getFeedback(conversationId: string, messageId: string): FeedbackData | null {
    try {
      const existing = this.getAllStoredData();
      return existing[conversationId]?.[messageId] || null;
    } catch (error) {
      console.error('Failed to get feedback from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Get all feedback for a conversation
   */
  static getAllFeedback(conversationId: string): Map<string, FeedbackData> {
    try {
      const existing = this.getAllStoredData();
      const conversationFeedback = existing[conversationId] || {};
      
      return new Map(Object.entries(conversationFeedback));
    } catch (error) {
      console.error('Failed to get conversation feedback from sessionStorage:', error);
      return new Map();
    }
  }

  /**
   * Remove feedback for a specific message
   */
  static removeFeedback(conversationId: string, messageId: string): void {
    try {
      const existing = this.getAllStoredData();
      
      if (existing[conversationId]) {
        delete existing[conversationId][messageId];
        
        // Clean up empty conversation entries
        if (Object.keys(existing[conversationId]).length === 0) {
          delete existing[conversationId];
        }
        
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(existing));
      }
    } catch (error) {
      console.error('Failed to remove feedback from sessionStorage:', error);
    }
  }

  /**
   * Clear all feedback for a conversation
   */
  static clearConversationFeedback(conversationId: string): void {
    try {
      const existing = this.getAllStoredData();
      delete existing[conversationId];
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(existing));
    } catch (error) {
      console.error('Failed to clear conversation feedback from sessionStorage:', error);
    }
  }

  /**
   * Clear all stored feedback data
   */
  static clearAllFeedback(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear all feedback from sessionStorage:', error);
    }
  }

  /**
   * Get the complete storage structure
   */
  private static getAllStoredData(): StorageStructure {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to parse feedback data from sessionStorage:', error);
      return {};
    }
  }

  /**
   * Check if feedback exists for a message
   */
  static hasFeedback(conversationId: string, messageId: string): boolean {
    return this.getFeedback(conversationId, messageId) !== null;
  }

  /**
   * Get feedback statistics for a conversation
   */
  static getConversationFeedbackStats(conversationId: string): {
    total: number;
    positive: number;
    negative: number;
  } {
    try {
      const feedback = this.getAllFeedback(conversationId);
      let positive = 0;
      let negative = 0;
      
      feedback.forEach((data) => {
        if (data.type === 'positive') positive++;
        else if (data.type === 'negative') negative++;
      });
      
      return {
        total: feedback.size,
        positive,
        negative
      };
    } catch (error) {
      console.error('Failed to get feedback stats:', error);
      return { total: 0, positive: 0, negative: 0 };
    }
  }
}
